import React, { Component, ErrorInfo, ReactNode } from 'react';
import { clientLogger } from '../services/clientLogger';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Robust React Error Boundary with automatic client logging to backend.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Send error report to server
    clientLogger.captureError({
      type: 'REACT_ERROR',
      level: 'error',
      message: error.message || 'React Render Component Crash',
      stack: error.stack || errorInfo.componentStack,
      metadata: {
        componentName: this.props.componentName || 'UnknownComponent',
        componentStack: errorInfo.componentStack
      }
    });

    // Force flush logger immediately so crash is recorded even if user immediately closes page
    clientLogger.flushImmediately();
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[450px] flex items-center justify-center p-6 w-full">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full border border-rose-150 shadow-xl shadow-rose-500/5 text-center flex flex-col items-center">
            <div className="w-16 h-16 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-500 mb-5 shadow-sm">
              <AlertTriangle className="w-8 h-8" />
            </div>

            <h2 className="text-xl font-bold text-slate-800 mb-2">
              Что-то пошло не так
            </h2>
            
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              В работе интерфейса произошла непредвиденная ошибка. Отчет о сбое уже отправлен разработчикам для устранения.
            </p>

            {this.state.error?.message && (
              <div className="w-full bg-slate-50 rounded-xl p-3 mb-6 border border-slate-200/80 text-left">
                <span className="text-[11px] font-mono text-rose-600 block break-words line-clamp-3">
                  {this.state.error.message}
                </span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
              <button
                type="button"
                onClick={this.handleReload}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-medium text-sm transition-all shadow-sm active:scale-95"
              >
                <RefreshCw className="w-4 h-4" />
                Обновить страницу
              </button>

              <a
                href="/"
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm transition-all active:scale-95"
              >
                <Home className="w-4 h-4" />
                На главную
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
